import { useLayoutEffect, useRef, useState } from "react";

/**
 * Renders a Recharts chart with explicit numeric width/height measured from
 * the wrapping container via ResizeObserver. Bypasses ResponsiveContainer to
 * avoid Recharts' "width(-1) and height(-1)" warning on first paint.
 *
 * Usage:
 *   <MeasuredChart height={280}>
 *     {({ width, height }) => (
 *       <LineChart width={width} height={height} data={...}>...</LineChart>
 *     )}
 *   </MeasuredChart>
 */
export default function MeasuredChart({ height, children }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setSize((s) => (s.width === rect.width ? s : { width: rect.width, height }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  return (
    <div ref={ref} style={{ width: "100%", height }}>
      {size.width > 0 ? children(size) : null}
    </div>
  );
}
